/* eslint-disable no-restricted-syntax */
import React from 'react';
import moment from 'moment';
import { isEmpty } from 'ramda';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { isMobile, isTablet } from 'react-device-detect';
import { useMutation, useQuery } from '@tanstack/react-query';
import Button from 'components/Button/Button';
import { paths } from 'routes';
import readAWSS3File from '../../utils/readAWSS3File';
import { showAlert, useAppContext } from '../../context/appContext';
import { LabTest } from '../../types/types';
import { formatArrayIntoSentence, hasOpenedLabReport } from '../../utils/utils';
import Icon from '../../components/Icon/Icon';
import { getLabs, updateLabRequest } from '../../api/api';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

async function getLabResult(id: string) {
  const labResults = (await getLabs({ id })) as LabTest[];

  if (!labResults || labResults.length === 0) return {};

  return {
    ...labResults[0],
    signedUrl: await readAWSS3File('lab-reports', labResults[0].url),
    name: formatArrayIntoSentence(labResults[0].types.map((lap: LabTest) => lap.name)),
  };
}

function LabResult() {
  const [{ patients }, dispatch] = useAppContext();
  const [numberOfPages, setNumberOfPages] = React.useState(0);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [hasLoadedPages, setHasLoadedPages] = React.useState(false);
  const { labTestId = '' } = useParams();

  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const visitId = params.get('visitId');

  const search = params.get('search');

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['lab-result', labTestId],
    queryFn: () => getLabResult(labTestId),
    enabled: !!labTestId,
  });

  const labTest = React.useMemo(() => {
    return (data || {}) as LabTest;
  }, [data]);

  const patient = patients?.find(fndPatient => fndPatient.id === labTest?.patientId);

  const { mutate: markLabReportAsOpenedFn } = useMutation(
    (variables: { id: string; payload: Record<string, any> }) =>
      updateLabRequest(variables.id, variables.payload),
    {
      onSettled: () => {},
    },
  );

  React.useEffect(() => {
    if (isEmpty(labTest) || hasOpenedLabReport([labTest])) return;

    const labURL = new URL(labTest.url);
    labURL.searchParams.set('status', 'OPENED');

    markLabReportAsOpenedFn({
      id: labTest.id,
      payload: { url: labURL.toString() },
    });
  }, [markLabReportAsOpenedFn, labTest]);

  const onDocumentLoadSuccess = (pdf: { numPages: number }) => {
    setHasLoadedPages(!isEmpty(pdf));
    setNumberOfPages(pdf.numPages);
  };

  const onDocumentError = (error: any) => {
    setHasLoadedPages(true);
    showAlert(
      dispatch,
      {
        show: true,
        message: `Oops. Issue loading file. Please reload the page to try again!  ${
          error?.message ? `(${stripUrlParams(error?.message)})` : ''
        } `,
        type: 'ERROR',
      },
      5000,
    );
  };

  function stripUrlParams(errorMessage: string): string {
    const urlPattern = /https?:\/\/[^\s]+/g; // Find the URL in the error message
    const match = errorMessage.match(urlPattern);
    if (match) {
      const baseUrl = match[0].split('?')[0]; // Strip off everything after the "?"
      return baseUrl;
    }
    return errorMessage;
  }

  const onNextPage = () => {
    if (pageNumber < numberOfPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const OnPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  return (
    <div className="relative pt-8 tablet:pt-14 pb-14 flex flex-col justify-center items-center">
      <button
        onClick={() => navigate(-1)}
        type="button"
        className="absolute top-14 left-0 hidden tablet:flex flex-row items-center"
      >
        <Icon id="goBack" className="w-8 h-8" />
      </button>

      <div className="w-full tablet:w-[90%] laptop:w-[50%] text-center">
        {isLoading || (labTest.signedUrl && !hasLoadedPages) ? (
          <div
            className="flex flex-col justify-center items-center absolute
            top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-14"
          >
            <span>Loading result. Please wait...</span>
            <Icon id="loader" className="w-10" />
          </div>
        ) : (
          <>
            <h2 className="font-semibold text-[20px] text-river-black">Lab Results</h2>
            <p className="text-sm mt-1 text-river-black">{labTest.name}</p>

            {!labTest.signedUrl && (
              <p className="text-sm mt-20 text-river-black">No report found.</p>
            )}
          </>
        )}
      </div>

      <div className="w-full tablet:w-[90%] laptop:w-[50%] mt-[40px]">
        {isSuccess && labTest.signedUrl ? (
          <div className="flex flex-col items-center">
            {patient && hasLoadedPages && (
              <div
                className="flex flex-col tablet:flex-row w-full items-start tablet:items-center justify-between mb-10
              bg-white shadow-[0_3px_12px_0px_rgba(0,0,0,0.05)] rounded-xl p-8 space-y-4 tablet:space-y-0"
              >
                <div className="flex flex-col items-start tablet:items-center text-center">
                  <h4 className="text-xs text-[#949494]">Patient name</h4>
                  <p>{`${patient.firstName} ${patient.lastName}`}</p>
                </div>
                <span className="hidden border-l-[1px] border-[#e9ecef] tablet:inline-flex h-10" />
                <div className="flex flex-col items-start tablet:items-center text-center">
                  <h4 className="text-xs text-[#949494]">Date of birth</h4>
                  <p>
                    {patient.patientDetails?.dateOfBirth
                      ? moment(patient.patientDetails?.dateOfBirth).format('D MMMM YYYY')
                      : '-'}
                  </p>
                </div>
                <span className="hidden border-l-[1px] border-[#e9ecef] tablet:inline-flex h-10" />
                <div className="flex flex-col items-start tablet:items-center text-center">
                  <h4 className="text-xs text-[#949494]">Date Requested</h4>
                  <p>{moment(labTest.createdAt).format('MM/DD/YYYY')}</p>
                </div>
              </div>
            )}

            <Document
              className={`flex flex-col items-center justify-center
                ${
                  hasLoadedPages
                    ? 'rounded-md bg-[#F2F4F5] p-8 shadow-[0_0_15px_10px_rgba(0,0,0,0.02)]'
                    : ''
                }`}
              loading=""
              file={labTest.signedUrl}
              // file="http://localhost:3000/sample.pdf"
              onLoadError={onDocumentError}
              onLoadSuccess={onDocumentLoadSuccess}
            >
              <Page
                pageNumber={pageNumber}
                className=""
                {...(isMobile && !isTablet && { width: 300 })}
              />
              <div className="flex flex-row items-center justify-center space-x-10 mt-[30px]">
                <button
                  className={`${
                    pageNumber === 1 ? 'opacity-40 pointer-events-none' : ''
                  } inline-flex items-center`}
                  type="button"
                  onClick={OnPrevPage}
                >
                  <Icon
                    id="arrowNext"
                    className="w-[15px] h-[15px] text-river-black rotate-180 mr-[10px]"
                  />
                  Prev
                </button>
                <button
                  className={`${
                    pageNumber === numberOfPages ? 'opacity-40 pointer-events-none' : ''
                  } inline-flex items-center`}
                  type="button"
                  onClick={onNextPage}
                >
                  Next
                  <Icon id="arrowNext" className="w-[15px] h-[15px] text-river-black ml-[10px]" />
                </button>
              </div>
            </Document>
          </div>
        ) : (
          <span />
        )}
      </div>

      {!isLoading && isSuccess && hasLoadedPages && visitId && search && (
        <div className="mt-10">
          <Button
            label="Add treatment notes"
            onClick={() =>
              navigate(
                `${paths.VISIT_RECORDS_URL_PATH.replace(
                  ':visitId',
                  visitId,
                )}/treatment-plan${search}`,
              )
            }
            className="!px-2 !min-w-[250px]"
          />
        </div>
      )}
    </div>
  );
}

export default LabResult;
